/**
 * CopilotPanel — AI Co-pilot panel for the Analysis stage
 *
 * Phase 4.4 — redesign-p4-analatt-44b
 * Wires to existing co-pilot backend capability (/intelligence/* endpoints)
 * Uses WhatIfForm, EngineRunDiff, ScenarioEnvelope, AccuracyMetrics from existing copilot components
 */

import React, { useState, useEffect } from 'react';
import { Sparkles, BarChart3, GitCompare, Target, Activity, RefreshCw } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useCopilot, WhatIfRequest, EngineRunDiffRequest } from '@/hooks/useCopilot';
import { BRAND_NAME } from '@/config/brand';

type CopilotTab = 'whatif' | 'diff' | 'scenarios' | 'accuracy';

interface CopilotPanelProps {
  reportId: string;
}

const COMMON_ENGINES = [
  { id: 'fair_settlement', label: 'Fair Settlement' },
  { id: 'bottom_up', label: 'Bottom-Up' },
  { id: 'dual_method', label: 'Dual Method' },
  { id: 'enhanced_claim', label: 'Enhanced Claim' },
  { id: 'epi', label: 'EPI (Extended Period)' },
];

const COMMON_OVERRIDES = [
  { field: 'restoration_days', label: 'Restoration Days', placeholder: 'e.g., 45' },
  { field: 'revenue_drop_pct', label: 'Revenue Drop %', placeholder: 'e.g., 0.25' },
  { field: 'expense_reduction_pct', label: 'Expense Reduction %', placeholder: 'e.g., 0.15' },
  { field: 'indemnity_days', label: 'Indemnity Days', placeholder: 'e.g., 60' },
];

export function CopilotPanel({ reportId }: CopilotPanelProps) {
  const [activeTab, setActiveTab] = useState<CopilotTab>('whatif');

  // What-If state
  const [engineId, setEngineId] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  // Diff state
  const [diffEngineId, setDiffEngineId] = useState('');
  const [runIdBefore, setRunIdBefore] = useState('');
  const [runIdAfter, setRunIdAfter] = useState('');

  const {
    isLoading,
    isLoadingWhatIf,
    isLoadingDiff,
    isLoadingScenarios,
    isLoadingAccuracy,
    error,
    whatIfResult,
    engineDiffResult,
    scenariosResult,
    accuracyResult,
    runEngineWhatIf,
    getEngineRunDiff,
    getPrewarroomScenarios,
    getEngineAccuracy,
  } = useCopilot();

  // Load initial data
  useEffect(() => {
    if (activeTab === 'scenarios') {
      getPrewarroomScenarios(reportId);
    } else if (activeTab === 'accuracy') {
      getEngineAccuracy();
    }
  }, [activeTab, reportId]);

  const handleRunWhatIf = async () => {
    if (!engineId) return;
    const parsedOverrides: Record<string, number | string> = {};
    for (const [key, value] of Object.entries(overrides)) {
      if (value.trim()) {
        const num = parseFloat(value);
        parsedOverrides[key] = isNaN(num) ? value : num;
      }
    }
    await runEngineWhatIf({
      reportId,
      engineId,
      overrides: parsedOverrides,
    });
  };

  const handleRunDiff = async () => {
    if (!diffEngineId) return;
    await getEngineRunDiff({
      reportId,
      engineId: diffEngineId,
      runIdBefore: runIdBefore || undefined,
      runIdAfter: runIdAfter || undefined,
    });
  };

  const tabs = [
    { id: 'whatif' as CopilotTab, label: 'What-If', icon: Sparkles },
    { id: 'diff' as CopilotTab, label: 'Compare', icon: GitCompare },
    { id: 'scenarios' as CopilotTab, label: 'Scenarios', icon: Target },
    { id: 'accuracy' as CopilotTab, label: 'Accuracy', icon: Activity },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-[var(--color-needs-human-tint)] p-2">
            <Sparkles className="h-5 w-5 text-[var(--color-brand)]" />
          </div>
          <div>
            <h2
              className="text-lg font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              AI Co-pilot
            </h2>
            <p className="text-xs text-[var(--color-text-muted)]">
              Exploratory analysis & scenario modeling
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-[var(--color-bg-elevated)] p-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-[var(--color-brand)] text-[var(--color-on-brand)]'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Error display */}
      {error && (
        <div className="rounded-lg bg-[rgba(255,102,0,0.08)] p-3">
          <p className="text-sm text-[var(--color-brand)]">{error.message}</p>
        </div>
      )}

      {/* What-If Tab */}
      {activeTab === 'whatif' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
            <h3 className="mb-4 text-sm font-medium text-[var(--color-text-primary)]">Run What-If Scenario</h3>

            <div className="mb-4">
              <label className="mb-1 block text-xs text-[var(--color-text-muted)]">Engine</label>
              <select
                value={engineId}
                onChange={(e) => setEngineId(e.target.value)}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
              >
                <option value="">Select engine…</option>
                {COMMON_ENGINES.map((e) => (
                  <option key={e.id} value={e.id}>{e.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-4 grid grid-cols-2 gap-3">
              {COMMON_OVERRIDES.map((o) => (
                <div key={o.field}>
                  <label className="mb-1 block text-xs text-[var(--color-text-muted)]">{o.label}</label>
                  <input
                    type="text"
                    value={overrides[o.field] || ''}
                    onChange={(e) => setOverrides((prev) => ({ ...prev, [o.field]: e.target.value }))}
                    placeholder={o.placeholder}
                    className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
                  />
                </div>
              ))}
            </div>

            <button
              onClick={handleRunWhatIf}
              disabled={isLoadingWhatIf || !engineId}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] disabled:opacity-50"
            >
              {isLoadingWhatIf ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Run What-If
            </button>
          </div>

          {whatIfResult && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
              <h3 className="mb-3 text-sm font-medium text-[var(--color-text-primary)]">Results</h3>

              <div className="mb-3 rounded-md bg-[var(--color-bg-elevated)] p-3">
                <p className="text-xs text-[var(--color-text-muted)]">Engine: {whatIfResult.engine_id}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Duration: {whatIfResult.duration_ms}ms</p>
              </div>

              {whatIfResult.diff_vs_current && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-[var(--color-text-muted)]">Changes vs Current:</p>
                  {Object.entries(whatIfResult.diff_vs_current).map(([key, value]) => {
                    const diff = value as { delta?: number; pct_change?: number; before?: number; after?: number };
                    return (
                      <div key={key} className="flex items-center justify-between rounded-md bg-[var(--color-bg-elevated)] px-3 py-2">
                        <span className="text-sm text-[var(--color-text-secondary)]">{key.replace(/_/g, ' ')}</span>
                        <div className="flex items-center gap-3">
                          {diff.before !== undefined && (
                            <span className="text-xs text-[var(--color-text-muted)]">
                              <FinancialValue value={diff.before} />
                            </span>
                          )}
                          <span className="text-[var(--color-brand)]">→</span>
                          {diff.after !== undefined && <FinancialValue value={diff.after} />}
                          {diff.pct_change !== undefined && (
                            <span className={`text-xs ${diff.pct_change > 0 ? 'text-[var(--color-brand)]' : 'text-[var(--color-verified)]'}`}>
                              {diff.pct_change > 0 ? '+' : ''}{diff.pct_change.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Diff Tab */}
      {activeTab === 'diff' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
            <h3 className="mb-4 text-sm font-medium text-[var(--color-text-primary)]">Compare Engine Runs</h3>

            <div className="mb-4">
              <label className="mb-1 block text-xs text-[var(--color-text-muted)]">Engine</label>
              <select
                value={diffEngineId}
                onChange={(e) => setDiffEngineId(e.target.value)}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
              >
                <option value="">Select engine…</option>
                {COMMON_ENGINES.map((e) => (
                  <option key={e.id} value={e.id}>{e.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-4 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-[var(--color-text-muted)]">Before Run ID (optional)</label>
                <input
                  type="text"
                  value={runIdBefore}
                  onChange={(e) => setRunIdBefore(e.target.value)}
                  placeholder="Latest if empty"
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-[var(--color-text-muted)]">After Run ID (optional)</label>
                <input
                  type="text"
                  value={runIdAfter}
                  onChange={(e) => setRunIdAfter(e.target.value)}
                  placeholder="Latest if empty"
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
                />
              </div>
            </div>

            <button
              onClick={handleRunDiff}
              disabled={isLoadingDiff || !diffEngineId}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] disabled:opacity-50"
            >
              {isLoadingDiff ? <RefreshCw className="h-4 w-4 animate-spin" /> : <GitCompare className="h-4 w-4" />}
              Compare Runs
            </button>
          </div>

          {engineDiffResult && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
              <h3 className="mb-3 text-sm font-medium text-[var(--color-text-primary)]">Diff Results</h3>
              <p className="mb-3 text-xs text-[var(--color-text-muted)]">{engineDiffResult.count} fields changed</p>

              <div className="space-y-2">
                {Object.entries(engineDiffResult.diff).map(([key, value]) => {
                  const diff = value as { delta?: number; pct_change?: number; before?: number; after?: number };
                  return (
                    <div key={key} className="flex items-center justify-between rounded-md bg-[var(--color-bg-elevated)] px-3 py-2">
                      <span className="text-sm text-[var(--color-text-secondary)]">{key.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-3">
                        {diff.before !== undefined && (
                          <span className="text-xs text-[var(--color-text-muted)]">
                            <FinancialValue value={diff.before} />
                          </span>
                        )}
                        <span className="text-[var(--color-brand)]">→</span>
                        {diff.after !== undefined && <FinancialValue value={diff.after} />}
                        {diff.pct_change !== undefined && (
                          <span className={`text-xs ${diff.pct_change > 0 ? 'text-[var(--color-brand)]' : 'text-[var(--color-verified)]'}`}>
                            {diff.pct_change > 0 ? '+' : ''}{diff.pct_change.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scenarios Tab */}
      {activeTab === 'scenarios' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-medium text-[var(--color-text-primary)]">Pre-War-Room Scenarios</h3>
              <button
                onClick={() => getPrewarroomScenarios(reportId)}
                disabled={isLoadingScenarios}
                className="flex items-center gap-1 rounded-md border border-[var(--color-brand)] px-2 py-1 text-xs text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)]"
              >
                <RefreshCw className={`h-3 w-3 ${isLoadingScenarios ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {scenariosResult?.baseline_available && (
              <div className="mb-4 rounded-lg border border-[var(--color-needs-human-tint-bd)] bg-[var(--color-needs-human-tint)] p-3">
                <p className="text-xs text-[var(--color-text-muted)]">Baseline Total</p>
                <FinancialValue value={scenariosResult.baseline_total} size="lg" />
              </div>
            )}

            <div className="space-y-3">
              {scenariosResult?.scenarios.map((scenario, idx) => (
                <div key={idx} className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)] p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[var(--color-text-primary)]">{scenario.name}</span>
                      <StatusBadge
                        text={scenario.scenario_type}
                        variant={scenario.scenario_type === 'baseline' ? 'done' : scenario.scenario_type === 'aggressive' ? 'attention' : 'neutral'}
                        size="sm"
                      />
                    </div>
                    {scenario.projected_total && <FinancialValue value={scenario.projected_total} />}
                  </div>
                  {scenario.description && (
                    <p className="text-xs text-[var(--color-text-muted)]">{scenario.description}</p>
                  )}
                  {scenario.diff_vs_baseline !== undefined && (
                    <p className={`text-xs ${scenario.diff_vs_baseline >= 0 ? 'text-[var(--color-verified)]' : 'text-[var(--color-brand)]'}`}>
                      {scenario.diff_vs_baseline >= 0 ? '+' : ''}
                      <FinancialValue value={scenario.diff_vs_baseline} /> vs baseline
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Accuracy Tab */}
      {activeTab === 'accuracy' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-medium text-[var(--color-text-primary)]">Engine Prediction Accuracy</h3>
              <button
                onClick={() => getEngineAccuracy()}
                disabled={isLoadingAccuracy}
                className="flex items-center gap-1 rounded-md border border-[var(--color-brand)] px-2 py-1 text-xs text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)]"
              >
                <RefreshCw className={`h-3 w-3 ${isLoadingAccuracy ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {accuracyResult?.overall && (
              <div className="mb-4 grid grid-cols-2 gap-3">
                <MetricCard
                  label="Total Settlements"
                  value={accuracyResult.overall.total_settlements}
                />
                <MetricCard
                  label="Mean Accuracy"
                  value={`${(accuracyResult.overall.mean_accuracy * 100).toFixed(1)}%`}
                  badge={{ text: 'Score', variant: 'done' }}
                />
              </div>
            )}

            <div className="space-y-3">
              {accuracyResult?.engines.map((engine, idx) => (
                <div key={idx} className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)] p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium text-[var(--color-text-primary)]">{engine.engine_name || engine.engine_id}</span>
                    <StatusBadge
                      text={`${(engine.closest_prediction_rate * 100).toFixed(0)}%`}
                      variant={engine.closest_prediction_rate > 0.7 ? 'done' : engine.closest_prediction_rate > 0.4 ? 'neutral' : 'attention'}
                      size="sm"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-[var(--color-text-muted)]">Predictions</p>
                      <p className="font-mono text-[var(--color-text-secondary)]">{engine.total_predictions}</p>
                    </div>
                    <div>
                      <p className="text-[var(--color-text-muted)]">MAV</p>
                      <p className="font-mono text-[var(--color-text-secondary)]">{engine.mean_absolute_variance.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[var(--color-text-muted)]">Accuracy</p>
                      <p className="font-mono text-[var(--color-text-secondary)]">
                        {engine.accuracy_score ? (engine.accuracy_score * 100).toFixed(1) : '—'}%
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CopilotPanel;
